import type {
  AiManagedGroupEligibleCampaign,
  AiManagedGroupPortfolioOption,
} from "@adpilot/shared";
import { MAX_MANAGED_GROUP_MEMBERS } from "@adpilot/shared";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  type CandidateAiFilter,
  type CandidateDeliveryFilter,
  type CandidatePortfolioFilter,
  ManagedGroupCandidateFilters,
} from "@/components/managed-group/ManagedGroupCandidatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getApiErrorMessage } from "@/lib/api-error";
import { CampaignTransferPane } from "./CampaignTransferPane";
import { CAMPAIGN_PAGE_SIZE, sortCandidates } from "./campaign-selection";

interface ScopeStepProps {
  campaignAsins: string;
  asinError: boolean;
  onCampaignAsinsChange: (value: string) => void;
  onSelectAllCampaigns: (signal: AbortSignal) => Promise<AiManagedGroupEligibleCampaign[]>;
  campaignDeliveryStatus: CandidateDeliveryFilter;
  campaignPortfolioId: CandidatePortfolioFilter;
  campaignAiStatus: CandidateAiFilter;
  campaignPage: number;
  campaignPageSize: number;
  campaignSearch: string;
  campaignTotal: number;
  campaigns: AiManagedGroupEligibleCampaign[];
  portfolioOptions: AiManagedGroupPortfolioOption[];
  isCampaignError: boolean;
  isPortfolioError: boolean;
  isLoading: boolean;
  name: string;
  selectedCampaignPage: number;
  selectedCampaigns: Map<string, AiManagedGroupEligibleCampaign>;
  shopId: string;
  shopItems: Array<{ value: string; label: string; country?: string }>;
  onAddCampaigns: (campaigns: AiManagedGroupEligibleCampaign[]) => void;
  onCampaignDeliveryStatusChange: (status: CandidateDeliveryFilter) => void;
  onCampaignPortfolioIdChange: (portfolioId: CandidatePortfolioFilter) => void;
  onCampaignAiStatusChange: (status: CandidateAiFilter) => void;
  onCampaignPageChange: (page: number) => void;
  onRetryCampaigns: () => void;
  onRetryPortfolios: () => void;
  onCampaignSearchChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onRemoveCampaigns: (campaignIds: string[]) => void;
  onSelectedCampaignPageChange: (page: number) => void;
  onShopChange: (shopId: string | null) => void;
}

export function ScopeStep({
  campaignAsins,
  asinError,
  onCampaignAsinsChange,
  onSelectAllCampaigns,
  campaignDeliveryStatus,
  campaignPortfolioId,
  campaignAiStatus,
  campaignPage,
  campaignPageSize,
  campaignSearch,
  campaignTotal,
  campaigns,
  portfolioOptions,
  isCampaignError,
  isPortfolioError,
  isLoading,
  name,
  selectedCampaignPage,
  selectedCampaigns,
  shopId,
  shopItems,
  onAddCampaigns,
  onCampaignDeliveryStatusChange,
  onCampaignPortfolioIdChange,
  onCampaignAiStatusChange,
  onCampaignPageChange,
  onRetryCampaigns,
  onRetryPortfolios,
  onCampaignSearchChange,
  onNameChange,
  onRemoveCampaigns,
  onSelectedCampaignPageChange,
  onShopChange,
}: ScopeStepProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const selectedShop = shopItems.find((item) => item.value === shopId);
  const selected = sortCandidates([...selectedCampaigns.values()], "delivery", "asc");
  const [pendingAdd, setPendingAdd] = useState<Map<string, AiManagedGroupEligibleCampaign>>(
    new Map(),
  );
  const [pendingRemove, setPendingRemove] = useState<Set<string>>(new Set());
  const selectionRequest = useRef<AbortController | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const filterKey = JSON.stringify([
    campaignSearch,
    campaignAsins,
    campaignDeliveryStatus,
    campaignPortfolioId,
    campaignAiStatus,
    shopId,
  ]);
  const previousFilter = useRef(filterKey);
  useEffect(() => {
    if (previousFilter.current !== filterKey) {
      previousFilter.current = filterKey;
      setPendingAdd(new Map());
      setSelectingAll(false);
    }
    return () => {
      selectionRequest.current?.abort();
    };
  }, [filterKey]);

  async function selectAll(): Promise<void> {
    selectionRequest.current?.abort();
    const controller = new AbortController();
    selectionRequest.current = controller;
    setSelectingAll(true);
    try {
      const matches = await onSelectAllCampaigns(controller.signal);
      if (!controller.signal.aborted)
        setPendingAdd(new Map(matches.map((campaign) => [campaign.id, campaign])));
    } catch (error) {
      if (!controller.signal.aborted) toast.error(await getApiErrorMessage(error));
    } finally {
      if (!controller.signal.aborted) setSelectingAll(false);
    }
  }

  const availablePageRows = campaigns.filter((campaign) => campaign.status !== "archived");
  const availableAllChecked =
    availablePageRows.length > 0 &&
    availablePageRows.every((campaign) => pendingAdd.has(campaign.id));
  const availableSomeChecked = availablePageRows.some((campaign) => pendingAdd.has(campaign.id));
  const selectedPageCount = Math.max(1, Math.ceil(selected.length / CAMPAIGN_PAGE_SIZE));
  const safeSelectedPage = Math.min(selectedCampaignPage, selectedPageCount);
  const selectedPageRows = selected.slice(
    (safeSelectedPage - 1) * CAMPAIGN_PAGE_SIZE,
    safeSelectedPage * CAMPAIGN_PAGE_SIZE,
  );
  const selectedAllChecked =
    selectedPageRows.length > 0 &&
    selectedPageRows.every((campaign) => pendingRemove.has(campaign.id));
  const selectedSomeChecked = selectedPageRows.some((campaign) => pendingRemove.has(campaign.id));

  useEffect(() => {
    if (selectedCampaignPage > selectedPageCount) {
      onSelectedCampaignPageChange(selectedPageCount);
    }
  }, [onSelectedCampaignPageChange, selectedCampaignPage, selectedPageCount]);

  function togglePendingAdd(campaign: AiManagedGroupEligibleCampaign): void {
    if (campaign.status === "archived") return;
    if (
      !pendingAdd.has(campaign.id) &&
      selected.length + pendingAdd.size >= MAX_MANAGED_GROUP_MEMBERS
    ) {
      toast.error(t("create.selectionLimit", { limit: MAX_MANAGED_GROUP_MEMBERS }));
      return;
    }
    setPendingAdd((current) => {
      const next = new Map(current);
      if (next.has(campaign.id)) next.delete(campaign.id);
      else next.set(campaign.id, campaign);
      return next;
    });
  }

  function togglePendingRemove(campaignId: string): void {
    setPendingRemove((current) => {
      const next = new Set(current);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  }

  function toggleAllPendingAdd(
    checked: boolean,
    selectableCampaigns: AiManagedGroupEligibleCampaign[],
  ): void {
    const next = new Map(pendingAdd);
    for (const campaign of selectableCampaigns) {
      if (checked) next.set(campaign.id, campaign);
      else next.delete(campaign.id);
    }
    if (selected.length + next.size > MAX_MANAGED_GROUP_MEMBERS) {
      toast.error(t("create.selectionLimit", { limit: MAX_MANAGED_GROUP_MEMBERS }));
      return;
    }
    setPendingAdd(next);
  }

  function toggleAllPendingRemove(checked: boolean): void {
    setPendingRemove((current) => {
      const next = new Set(current);
      for (const campaign of selectedPageRows) {
        if (checked) next.add(campaign.id);
        else next.delete(campaign.id);
      }
      return next;
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 px-[22px] py-4">
      <div data-slot="managed-group-basics" className="grid shrink-0 grid-cols-2 gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="managed-group-name" className="text-[11px] leading-[14px]">
            {t("create.name")}
          </Label>
          <Input
            id="managed-group-name"
            name="managed-group-name"
            autoComplete="off"
            value={name}
            maxLength={100}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder={t("create.namePlaceholder")}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <Label className="text-[11px] leading-[14px]">{t("create.shop")}</Label>
          <Select items={shopItems} value={shopId} onValueChange={onShopChange}>
            <SelectTrigger aria-label={t("create.shop")} className="h-9 w-full">
              <SelectValue>
                {selectedShop?.country && (
                  <Badge variant="neutral" size="compact">
                    {selectedShop.country}
                  </Badge>
                )}
                {selectedShop?.label}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {shopItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.country && (
                      <Badge variant="neutral" size="compact">
                        {item.country}
                      </Badge>
                    )}
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      <section
        aria-label={t("create.transferLabel")}
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-line"
      >
        <ManagedGroupCandidateFilters
          layout="transfer"
          portfolioOptions={portfolioOptions}
          search={campaignSearch}
          deliveryStatus={campaignDeliveryStatus}
          portfolioId={campaignPortfolioId}
          aiStatus={campaignAiStatus}
          onSearchChange={onCampaignSearchChange}
          onDeliveryStatusChange={onCampaignDeliveryStatusChange}
          onPortfolioIdChange={onCampaignPortfolioIdChange}
          onAiStatusChange={onCampaignAiStatusChange}
        />

        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-3 py-2">
          <Label htmlFor="managed-group-asins" className="shrink-0 text-xs">
            {t("create.asinFilter")}
          </Label>
          <Input
            id="managed-group-asins"
            className="h-8 min-w-48 flex-1"
            value={campaignAsins}
            placeholder={t("create.asinPlaceholder")}
            aria-invalid={asinError}
            aria-describedby="managed-group-asin-hint"
            onChange={(event) => onCampaignAsinsChange(event.target.value)}
          />
          <p
            id="managed-group-asin-hint"
            className={
              asinError
                ? "w-full text-xs text-destructive-foreground"
                : "w-full text-xs text-muted-foreground"
            }
          >
            {t(asinError ? "create.asinInvalid" : "create.asinHint")}
          </p>
        </div>
        <div
          data-slot="campaign-transfer-grid"
          className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_112px_minmax(0,1fr)]"
        >
          <CampaignTransferPane
            kind="available"
            title={t("create.availableCampaigns")}
            countLabel={t("create.availableCount", { count: campaignTotal })}
            actions={
              <>
                <Button
                  variant="link"
                  size="xs"
                  className="h-6 px-0 text-primary"
                  loading={selectingAll}
                  disabled={
                    isLoading ||
                    isCampaignError ||
                    isPortfolioError ||
                    asinError ||
                    campaignTotal === 0 ||
                    selected.length >= MAX_MANAGED_GROUP_MEMBERS
                  }
                  onClick={() => void selectAll()}
                >
                  {t("create.selectAllResults")}
                </Button>
                <span className="ml-auto whitespace-nowrap tabular-nums text-muted-foreground">
                  {t("create.pendingCount", { count: pendingAdd.size })}
                </span>
                {pendingAdd.size > 0 && (
                  <Button
                    variant="link"
                    size="xs"
                    className="h-6 px-0 text-muted-foreground"
                    disabled={selectingAll}
                    onClick={() => setPendingAdd(new Map())}
                  >
                    {t("create.clearChecks")}
                  </Button>
                )}
              </>
            }
            rows={asinError ? [] : campaigns}
            checkedIds={new Set(pendingAdd.keys())}
            selectionDisabled={selectingAll || asinError}
            isError={isCampaignError || isPortfolioError}
            errorMessage={t(
              isCampaignError && isPortfolioError
                ? "candidateFilters.campaignAndPortfolioLoadFailed"
                : isCampaignError
                  ? "candidateFilters.campaignLoadFailed"
                  : "candidateFilters.portfolioLoadFailed",
            )}
            isLoading={isLoading}
            emptyText={t(asinError ? "create.asinInvalid" : "create.noCampaigns")}
            page={campaignPage}
            pageCount={Math.max(1, Math.ceil(campaignTotal / campaignPageSize))}
            pageSize={campaignPageSize}
            total={campaignTotal}
            checkboxLabel={(campaign) => t("create.selectForAdd", { name: campaign.name })}
            selectAllLabel={t("create.selectAllAvailable")}
            allRowsChecked={availableAllChecked}
            someRowsChecked={availableSomeChecked}
            onPageChange={onCampaignPageChange}
            onRetry={() => {
              if (isCampaignError) onRetryCampaigns();
              if (isPortfolioError) onRetryPortfolios();
            }}
            onToggleAll={(checked) => toggleAllPendingAdd(checked, availablePageRows)}
            onToggle={togglePendingAdd}
          />

          <fieldset
            data-slot="campaign-transfer-controls"
            data-orientation="vertical"
            aria-label={t("create.transferActions")}
            className="m-0 flex min-w-0 flex-col items-center justify-center gap-2 border-x border-line bg-table-footer px-2 py-3"
          >
            <Button
              size="xs"
              className="h-8 w-full gap-1.5 px-2 tabular-nums"
              aria-label={t("create.addSelectedCampaigns")}
              disabled={pendingAdd.size === 0 || selectingAll || asinError}
              onClick={() => {
                onAddCampaigns([...pendingAdd.values()]);
                setPendingAdd(new Map());
              }}
            >
              <span>{t("create.addCount", { count: pendingAdd.size })}</span>
              <IconChevronRight
                className="size-3.5"
                aria-hidden
                width={13}
                height={13}
                viewBox="0 0 24 24"
                strokeWidth={1.7}
              />
            </Button>
            <Button
              size="xs"
              variant="outline"
              className="h-8 w-full gap-1.5 px-2 tabular-nums"
              aria-label={t("create.removeSelectedCampaigns")}
              disabled={pendingRemove.size === 0 || selectingAll}
              onClick={() => {
                onRemoveCampaigns([...pendingRemove]);
                setPendingRemove(new Set());
              }}
            >
              <IconChevronLeft
                className="size-3.5"
                aria-hidden
                width={13}
                height={13}
                viewBox="0 0 24 24"
                strokeWidth={1.7}
              />
              <span>{t("create.removeCount", { count: pendingRemove.size })}</span>
            </Button>
          </fieldset>

          <CampaignTransferPane
            kind="selected"
            title={t("create.selectedCampaigns")}
            countLabel={t("create.selectedCapacity", {
              count: selected.length,
              limit: MAX_MANAGED_GROUP_MEMBERS,
            })}
            actions={
              <>
                <span className="mr-auto whitespace-nowrap tabular-nums text-muted-foreground">
                  {t("create.pendingCount", { count: pendingRemove.size })}
                </span>
                <Button
                  variant="link"
                  size="xs"
                  className="h-6 px-0 text-muted-foreground"
                  disabled={selected.length === 0 || selectingAll}
                  onClick={() => {
                    onRemoveCampaigns(selected.map((campaign) => campaign.id));
                    setPendingRemove(new Set());
                  }}
                >
                  {t("create.removeAll")}
                </Button>
              </>
            }
            selectionDisabled={selectingAll}
            rows={selectedPageRows}
            checkedIds={pendingRemove}
            isError={false}
            isLoading={false}
            emptyText={t("create.noneSelected")}
            page={safeSelectedPage}
            pageCount={selectedPageCount}
            pageSize={CAMPAIGN_PAGE_SIZE}
            total={selected.length}
            checkboxLabel={(campaign) => t("create.selectForRemove", { name: campaign.name })}
            selectAllLabel={t("create.selectAllSelected")}
            allRowsChecked={selectedAllChecked}
            someRowsChecked={selectedSomeChecked}
            onPageChange={onSelectedCampaignPageChange}
            onToggleAll={toggleAllPendingRemove}
            onToggle={(campaign) => togglePendingRemove(campaign.id)}
          />
        </div>
      </section>
    </div>
  );
}
